import type { FlowToken, HomeyAPI, ItemProps } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__.js';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type FlowTokenProps = ItemProps<FlowToken>;

type FlowTokensData = { [key: string]: FlowToken };
type FlowTokensById = { [key: string]: FlowTokenProps };

interface State {
  data: FlowTokensData | null;
  byId: FlowTokensById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowTokensStoreState = State;

export class FlowTokensStore extends BaseApiStore<State, FlowToken> {
  constructor() {
    super('flow-tokens');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.flowtoken });
      if (abortController.signal.aborted) return;

      const flowTokensPromise = api.flowtoken.getFlowTokens({ $cache: cache });
      const [flowTokensData] = await Promise.all([flowTokensPromise]);

      if (abortController.signal.aborted) return;

      const flowTokens = Object.values(flowTokensData);

      const data: FlowTokensData = {};
      const byId: FlowTokensById = {};

      for (const flowTokenInstance of flowTokens) {
        const flowTokenReference = flowTokenInstance.getProps();

        data[flowTokenReference.id] = flowTokenInstance;
        byId[flowTokenReference.id] = flowTokenReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flowtoken.on('flowtoken.create', this.handleCreate);
    api.flowtoken.on('flowtoken.update', this.handleUpdate);
    api.flowtoken.on('flowtoken.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flowtoken.off('flowtoken.create', this.handleCreate);
    api.flowtoken.off('flowtoken.update', this.handleUpdate);
    api.flowtoken.off('flowtoken.delete', this.handleDelete);
  }

  getInstanceFromCache(flowToken: { id: string }) {
    return this.getCache()[flowToken.id];
  }

  getCache(): Readonly<FlowTokensData> {
    const api = this.getApi();

    return api.flowtoken.getCachedItems('flowtoken');
  }

  handleCreate = (createdFlowToken: FlowToken) => {
    this.DEBUG && console.info(`create:flowtoken:${createdFlowToken.id}`);
    const state = this.get();
    const flowTokenInstance = this.getInstanceFromCache(createdFlowToken);

    if (flowTokenInstance == null) return;

    const flowTokenReference = flowTokenInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdFlowToken.id]: flowTokenInstance,
      },
      byId: {
        ...state.byId,
        [createdFlowToken.id]: flowTokenReference,
      },
    });
  };

  handleUpdate = (updatedFlowToken: FlowToken) => {
    this.DEBUG && console.info(`update:flowtoken:${updatedFlowToken.id}`);

    this.addToQueue(updatedFlowToken.id, updatedFlowToken);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlowToken] of Object.entries(updateQueue)) {
        const flowTokenInstance = this.getInstanceFromCache(queuedFlowToken);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flowtoken:missing-data`);
          continue;
        }

        if (flowTokenInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flowtoken:unknown:${id}`);
          continue;
        }

        const flowTokenReference = flowTokenInstance.getProps();

        nextById[id] = flowTokenReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flowtoken:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlowToken: Pick<FlowToken, 'id'>) => {
    this.DEBUG && console.info(`delete:flowtoken:${deletedFlowToken.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flowtoken:missing-data`);
      return;
    }

    const flowTokenInstance = state.data[deletedFlowToken.id];

    if (flowTokenInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowTokenInstance.id];
    delete nextById[flowTokenInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const flowTokens = Object.values(cache ?? {});
    const data: FlowTokensData = {};
    const byId: FlowTokensById = {};

    for (const flowTokenInstance of flowTokens) {
      const flowTokenReference = flowTokenInstance.getProps();
      data[flowTokenReference.id] = flowTokenInstance;
      byId[flowTokenReference.id] = flowTokenReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowTokensStore = new FlowTokensStore();
